import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface AppShellProps {
  children: ReactNode;
  onSignOut: () => Promise<void>;
  userName?: string | null;
}

const navLinkClass = ({ isActive }: { isActive: boolean }) => cn(
  'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
  isActive
    ? 'bg-primary text-primary-foreground'
    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
);

export const AppShell = ({ children, onSignOut, userName }: AppShellProps) => (
  <div className="min-h-screen bg-background">
    <div className="relative flex min-h-screen flex-col lg:flex-row">
      <aside className="relative z-10 w-full border-b border-border bg-card/80 px-6 py-6 backdrop-blur lg:min-h-screen lg:w-64 lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between lg:flex-col lg:items-start lg:gap-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Filesync</p>
            <h1 className="text-xl font-semibold">Web Portal</h1>
          </div>
          <div className="hidden text-sm text-muted-foreground lg:block">
            {userName ? `Signed in as ${userName}` : 'Signed in'}
          </div>
        </div>
        <nav className="mt-6 flex gap-2 lg:mt-10 lg:flex-col">
          <NavLink to="/repos" className={navLinkClass}>
            Repositories
          </NavLink>
        </nav>
        <div className="mt-6 hidden lg:block">
          <Button
            variant="outline"
            className="w-full justify-center"
            onClick={() => {
              void onSignOut();
            }}
          >
            Sign out
          </Button>
        </div>
      </aside>

      <div className="relative flex-1 overflow-hidden">
        <div className="pointer-events-none absolute right-0 top-0 h-64 w-64 -translate-y-1/3 translate-x-1/3 rounded-full bg-primary/15 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-1/3 h-72 w-72 translate-y-1/3 rounded-full bg-secondary/20 blur-3xl" />

        <header className="relative z-10 flex items-center justify-between border-b border-border bg-background/70 px-6 py-4 backdrop-blur">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Workspace</p>
            <p className="text-lg font-semibold">Filesystem Viewer</p>
          </div>
          <div className="flex items-center gap-3">
            {userName ? <span className="hidden text-sm text-muted-foreground md:block">{userName}</span> : null}
            <Button
              variant="outline"
              className="lg:hidden"
              onClick={() => {
                void onSignOut();
              }}
            >
              Sign out
            </Button>
          </div>
        </header>

        <main className="relative z-10 px-6 py-8">
          {children}
        </main>
      </div>
    </div>
  </div>
);
